// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Answers: what is driving ten minutes actually worth? A median hides the
// dispersion, and the dispersion IS the saving.

import { loadSpread, prefs } from '../data';
import type { SpreadRow } from '../types';
import { chartSvg, mark, svgEl, text, ticks } from '../chart';
import { histogram } from '../layout';
import { cents, esc, escTip, longDate, num, priceColour, tankSaving } from '../format';
import { glossaryTerm } from '../glossary';
import { openStation } from '../components/drawer';

export async function renderSpread(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const { days } = await loadSpread();
  if (!days.length) { root.innerHTML = '<p class="empty">No single-day snapshot available.</p>'; return; }

  let state = prefs.get('spreadState', days[0].state);
  if (!days.some((d) => d.state === state)) state = days[0].state;
  let region = 'ALL';

  root.innerHTML = `
    <div class="view-head">
      <h1>What driving ten minutes is worth</h1>
      <p>Every station's unleaded price on a single day. The middle price is a summary; the
      ${glossaryTerm('spread')} is the money. Hover any dot for the station.</p>
    </div>
    <div id="sp-cards"></div>
    <div class="panel">
      <div class="controls">
        <div class="seg" id="sp-state" role="group" aria-label="State">
          ${days.map((d) => `<button type="button" data-state="${esc(d.state)}">${d.state === 'WA' ? 'Western Australia' : 'Queensland'}</button>`).join('')}
        </div>
        <label for="sp-region">Region</label>
        <select id="sp-region"></select>
      </div>
      <div class="chart-scroll"><div class="chart-frame" id="sp-hist"></div></div>
      <div class="legend" id="sp-legend"></div>
    </div>
    <div class="panel">
      <div class="panel-head"><h2>Cheapest and dearest on the day</h2><p>The ten either side, from the same snapshot. Click for the station.</p></div>
      <div class="grid-2" id="sp-lists"></div>
    </div>
  `;

  const seg = root.querySelector('#sp-state') as HTMLElement;
  const regionSel = root.querySelector('#sp-region') as HTMLSelectElement;
  const syncSeg = () => seg.querySelectorAll('button').forEach((b) => {
    b.setAttribute('aria-pressed', String((b as HTMLElement).dataset.state === state));
  });
  seg.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('button') as HTMLElement | null;
    if (!btn) return;
    state = btn.dataset.state ?? state;
    region = 'ALL';
    prefs.set('spreadState', state);
    syncSeg();
    fillRegions();
    draw();
  });
  regionSel.addEventListener('change', () => { region = regionSel.value; draw(); });

  function fillRegions(): void {
    const day = days.find((d) => d.state === state)!;
    const regions = [...new Set(day.rows.map((r) => r.r))].sort();
    regionSel.innerHTML = `<option value="ALL">All regions</option>${regions.map((r) => `<option value="${esc(r)}">${esc(r)}</option>`).join('')}`;
    regionSel.value = region;
  }

  function draw(): void {
    const day = days.find((d) => d.state === state)!;
    const rows = region === 'ALL' ? day.rows : day.rows.filter((r) => r.r === region);
    renderCards(root.querySelector('#sp-cards')!, rows, day.date);
    renderHistogram(root.querySelector('#sp-hist') as HTMLElement, rows, root.querySelector('#sp-legend') as HTMLElement);
    renderLists(root.querySelector('#sp-lists')!, rows, day.state as 'WA' | 'QLD', day.date);
  }
  syncSeg();
  fillRegions();
  draw();
}

function renderCards(host: HTMLElement, rows: SpreadRow[], date: string): void {
  if (!rows.length) { host.innerHTML = '<p class="empty">No stations reported in this region on that day.</p>'; return; }
  const prices = rows.map((r) => r.p).sort((a, b) => a - b);
  const mid = prices[Math.floor(prices.length / 2)];
  const p10 = prices[Math.floor(prices.length * 0.1)];
  const p90 = prices[Math.floor(prices.length * 0.9)];
  host.innerHTML = `
    <div class="grid-cards" style="margin-bottom:var(--space-lg)">
      <div class="stat-card"><b>${cents(mid)}c</b><span>middle price, ${rows.length} stations</span></div>
      <div class="stat-card"><b>${cents(prices[prices.length - 1] - prices[0])}c</b><span>full range on one day</span></div>
      <div class="stat-card"><b>${cents(p90 - p10)}c</b><span>range ignoring the extreme tenth each end</span></div>
      <div class="stat-card"><b>${tankSaving(p90 - p10)}</b><span>a tank, that realistic range</span></div>
    </div>
    <p class="brand-sub" style="margin:-8px 0 var(--space-lg)">Snapshot for ${esc(longDate(date))}.</p>
  `;
}

function renderHistogram(host: HTMLElement, rows: SpreadRow[], legendHost: HTMLElement): void {
  host.innerHTML = '';
  if (!rows.length) return;
  const values = rows.map((r) => r.p);
  const width = 900;
  const height = 320;
  const padL = 48; const padR = 16; const padT = 18; const padB = 48;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // 2 c/L bins, which is the granularity retailers actually price at.
  const bins = histogram(values, 20, innerW, lo, hi);
  const maxCount = Math.max(...bins.map((b) => b.count), 1);
  const svg = chartSvg(width, height, 'Distribution of station prices on one day');

  for (const t of ticks(0, maxCount, 5)) {
    const y = padT + innerH - (t / maxCount) * innerH;
    svg.appendChild(svgEl('line', { x1: padL, x2: padL + innerW, y1: y, y2: y, class: 'gridline' }));
    svg.appendChild(text(padL - 8, y + 4, String(Math.round(t)), { 'text-anchor': 'end', 'font-size': 11 }));
  }

  for (const b of bins) {
    if (!b.count) continue;
    const h = (b.count / maxCount) * innerH;
    const rect = svgEl('rect', {
      x: padL + b.x + 0.5, y: padT + innerH - h, width: Math.max(1, b.w - 1), height: h, rx: 2,
      fill: priceColour((b.lo + b.hi) / 2, lo, hi),
    });
    mark(rect, `${cents(b.lo)}–${cents(b.hi)}c\n${b.count} station${b.count === 1 ? '' : 's'}\n${((b.count / rows.length) * 100).toFixed(1)}% of the market`);
    svg.appendChild(rect);
  }

  const step = Math.max(1, Math.round(bins.length / 8));
  bins.forEach((b, i) => {
    if (i % step) return;
    svg.appendChild(text(padL + b.x + b.w / 2, height - 28, cents(b.lo, 0), { 'text-anchor': 'middle', 'font-size': 11 }));
  });
  svg.appendChild(text(padL + innerW / 2, height - 8, 'cents per litre', { 'text-anchor': 'middle', 'font-size': 11, fill: '#857f78' }));
  svg.appendChild(text(padL - 8, padT - 4, 'stations', { 'text-anchor': 'end', 'font-size': 11, fill: '#857f78' }));
  host.appendChild(svg);

  legendHost.innerHTML = `<span class="key" style="color:var(--text-tertiary)">Each bar is a 2 cent band — the granularity stations actually price at. ${num(rows.length)} stations in this snapshot.</span>`;
}

function renderLists(host: HTMLElement, rows: SpreadRow[], state: 'WA' | 'QLD', date: string): void {
  if (!rows.length) { host.innerHTML = ''; return; }
  const sorted = [...rows].sort((a, b) => a.p - b.p);
  const list = (items: SpreadRow[], title: string, good: boolean) => `
    <div>
      <h3 style="font-size:var(--font-size-lg);margin-bottom:var(--space-sm)">${esc(title)}</h3>
      <div class="table-scroll">
        <table><tbody>${items.map((r, i) => `<tr class="clickable" data-idx="${rows.indexOf(r)}">
          <td class="n rank">${i + 1}</td>
          <td><span class="truncate" style="display:block;max-width:20ch">${esc(r.n)}</span></td>
          <td>${esc(r.s)}</td>
          <td class="n" style="color:${good ? 'var(--status-good)' : 'var(--status-bad)'};font-weight:650"
            data-tip="${escTip(`${r.n}\n${r.s} · ${r.b || 'Independent'}\n${cents(r.p)}c`)}">${cents(r.p)}</td>
        </tr>`).join('')}</tbody></table>
      </div>
    </div>`;
  host.innerHTML = list(sorted.slice(0, 10), 'Ten cheapest', true) + list(sorted.slice(-10).reverse(), 'Ten dearest', false);
  host.querySelectorAll('tr.clickable').forEach((tr) => {
    tr.addEventListener('click', () => {
      const r = rows[Number((tr as HTMLElement).dataset.idx)];
      if (!r) return;
      openStation({
        n: r.n, b: r.b, s: r.s, pc: '', r: r.r, st: state, lat: r.lat, lon: r.lon, d: date,
        p: { ULP: r.p },
      });
    });
  });
}
